import eventTrackingErrorsModule from '../../../services/eventTrackingErrors';
const moment = require('moment');

export default {
  data() {
    return {
      fetchingData: true,
      errorDetails: null,
      eventName: null,
      dateRange: null,
      defaultDuration: {
        start: null,
        end: null
      },
      currentPage: 1,
      pageSize: 100,
      showRawDataModal: false,
      selectedError: null,
      chartOptions: null,
      activeErrorTab: 'errorOutput',
      shouldFetchChartData: true, // Track if we should fetch chart data

      // Timing filter properties
      timingFilter: 'last-7',
      customTimeRange: null,
      displayTimeRange: null,
      eventTrackingErrorsTimingOptions: [
        {
          label: 'Weekly Durations',
          options: [
            {
              title: 'Last 7 Days',
              value: 'last-7'
            },
            {
              title: 'Current Week',
              value: 'this-week'
            },
            {
              value: 'last-week',
              title: 'Last Week'
            }
          ]
        },
        {
          label: 'Monthly Durations',
          options: [
            {
              title: 'Last 30 Days',
              value: 'last-30'
            },
            {
              value: 'this-month',
              title: 'Current Month'
            },
            {
              value: 'last-month',
              title: 'Last Month'
            }
          ]
        },
        {
          label: 'Custom',
          options: [
            {
              value: 'custom',
              title: 'Custom Range'
            }
          ]
        }
      ]
    };
  },
  watch: {
    timingFilter: {
      handler: function () {
        if (this.timingFilter != 'custom') {
          this.currentPage = 1;
          this.shouldFetchChartData = true; // Fetch chart data when timing filter changes
          this.fetchErrorDetails();
        }
      }
    }
  },
  methods: {
    // Calculate date range based on timing filter
    calculateDateRange() {
      let start = null;
      let end = null;

      if (this.timingFilter === 'custom') {
        if (this.customTimeRange && this.customTimeRange.length === 2) {
          start = moment(this.customTimeRange[0]).startOf('day');
          end = moment(this.customTimeRange[1]).endOf('day');

          // Validate maximum 90 days for custom range
          const diff = end.diff(start, 'days');
          if (diff > 90) {
            this.warningToast('Maximum 3 months are allowed for analysis.');
            return null;
          }
        } else {
          return null;
        }
      } else if (this.timingFilter === 'this-week') {
        start = moment().startOf('week');
        end = moment().endOf('day');
      } else if (this.timingFilter === 'last-week') {
        start = moment().subtract(1, 'week').startOf('week');
        end = moment().subtract(1, 'week').endOf('week');
      } else if (this.timingFilter === 'this-month') {
        start = moment().startOf('month');
        end = moment().endOf('day');
      } else if (this.timingFilter === 'last-month') {
        start = moment().subtract(1, 'month').startOf('month');
        end = moment().subtract(1, 'month').endOf('month');
      } else if (this.timingFilter === 'last-7') {
        start = moment().subtract(7, 'days').startOf('day');
        end = moment().endOf('day');
      } else if (this.timingFilter === 'last-30') {
        start = moment().subtract(30, 'days').startOf('day');
        end = moment().endOf('day');
      }

      if (start && end) {
        this.defaultDuration.start = start.toISOString();
        this.defaultDuration.end = end.toISOString();
        this.dateRange = [start.toDate(), end.toDate()];

        // Set display time range
        this.displayTimeRange = {
          start: start.format('DD-MMM-YYYY'),
          end: end.format('DD-MMM-YYYY')
        };

        return true;
      }
      return false;
    },

    async fetchErrorDetails() {
      // Calculate date range based on timing filter
      if (!this.calculateDateRange()) {
        this.fetchingData = false;
        return;
      }

      this.fetchingData = true;
      try {
        const params = {
          duration: this.defaultDuration,
          eventName: this.eventName,
          pageOptions: {
            pageNumber: this.currentPage,
            pageSize: this.pageSize
          },
          // Only include daywise data when we actually need to update the chart
          // Skip on pagination to reduce BigQuery billing
          includeDaywiseData: this.shouldFetchChartData
        };

        const response = await eventTrackingErrorsModule.getErrorDetails(params);
        this.errorDetails = response.data;

        // Prepare chart data only if we fetched it
        if (this.shouldFetchChartData) {
          this.prepareChartData();
          this.shouldFetchChartData = false; // Reset flag after fetching
        }
      } catch (error) {
        console.error('Error fetching error details:', error);
        this.errorToast('Failed to fetch event tracking error details.');
      } finally {
        this.fetchingData = false;
      }
    },

    prepareChartData() {
      if (!this.errorDetails) {
        this.chartOptions = null;
        return;
      }

      // Debug: Log the error details to see what we're getting
      console.log('Error details for chart:', this.errorDetails);
      console.log('Daywise count array:', this.errorDetails.daywiseCountArr);

      // Check if daywiseCountArr exists and has data
      if (!this.errorDetails.daywiseCountArr || !Array.isArray(this.errorDetails.daywiseCountArr) || this.errorDetails.daywiseCountArr.length === 0) {
        console.log('No daywise count data available - chartOptions will be null');
        this.chartOptions = null;
        return;
      }

      // Sort by date ascending for chart
      const sortedData = [...this.errorDetails.daywiseCountArr].sort((a, b) => {
        return moment(a.date).valueOf() - moment(b.date).valueOf();
      });

      // Extract categories and data
      const categories = sortedData.map((item) => moment(item.date).format('DD MMM'));
      const data = sortedData.map((item) => parseInt(item.count));

      // Create Highcharts options
      this.chartOptions = {
        chart: {
          type: 'column',
          height: 300
        },
        title: {
          text: null
        },
        legend: {
          enabled: false
        },
        xAxis: {
          categories: categories,
          title: {
            text: 'Date'
          }
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Error Count'
          }
        },
        tooltip: {
          headerFormat: '<b>{point.x}</b><br/>',
          pointFormat: 'Errors: {point.y}'
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: false
            },
            enableMouseTracking: true,
            borderWidth: 0
          }
        },
        series: [
          {
            name: 'Errors',
            data: data,
            color: '#0080FF'
          }
        ]
      };
    },

    onDateRangeChange() {
      if (this.timingFilter === 'custom' && this.customTimeRange && this.customTimeRange.length === 2) {
        this.currentPage = 1;
        this.shouldFetchChartData = true; // Fetch chart data when date range changes
        this.fetchErrorDetails();
      }
    },

    setLast7Days() {
      this.timingFilter = 'last-7';
      this.calculateDateRange();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.shouldFetchChartData = false; // Don't fetch chart data on pagination
      this.fetchErrorDetails();
    },

    onErrorRowClick(row) {
      this.selectedError = row;
      this.showRawDataModal = true;
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '--';

      // Handle different formats from BigQuery
      let parsedDate = moment(dateTime);

      console.log('Date Formatted:', dateTime, '==>', parsedDate.format('DD-MMM-YYYY HH:mm:ss'));
      if (!parsedDate.isValid()) {
        return '--';
      }

      return parsedDate.format('DD-MMM-YYYY HH:mm:ss');
    },

    getErrorMessage(row) {
      // Use error_details column (JSON string) - parse and display as string
      if (row.error_details) {
        try {
          // Parse the JSON string
          let errorDetails = row.error_details;
          if (typeof errorDetails === 'string') {
            errorDetails = JSON.parse(errorDetails);
          }

          // If it's an object, convert to readable string format
          if (typeof errorDetails === 'object' && errorDetails !== null) {
            // Try to get a meaningful message field first
            if (errorDetails.message) {
              return String(errorDetails.message);
            }
            if (errorDetails.error) {
              return String(errorDetails.error);
            }
            if (errorDetails.errorMessage) {
              return String(errorDetails.errorMessage);
            }
            // Convert the entire object to a readable string
            return JSON.stringify(errorDetails, null, 2);
          }

          // If it's already a string (or number), return it
          return String(errorDetails);
        } catch (e) {
          // If parsing fails, return the raw string
          return String(row.error_details);
        }
      }

      // Fallback to error_message if error_details is not available
      if (row.error_message && row.error_message !== '--' && row.error_message.trim() !== '') {
        return row.error_message;
      }

      return '--';
    },

    getFormattedDateRange() {
      if (this.defaultDuration.start && this.defaultDuration.end) {
        const start = moment(this.defaultDuration.start).format('DD MMM');
        const end = moment(this.defaultDuration.end).format('DD MMM');
        return `${start} - ${end}`;
      }
      return '--';
    },

    getNumberWithComma(num) {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    formatJson(data) {
      if (!data) return '';
      try {
        if (typeof data === 'string') {
          const parsed = JSON.parse(data);
          return JSON.stringify(parsed, null, 2);
        }
        return JSON.stringify(data, null, 2);
      } catch (e) {
        return data;
      }
    },

    downloadJson() {
      if (!this.selectedError) return;

      const dataStr = JSON.stringify(this.selectedError, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `error_${this.selectedError.created_at_date || 'data'}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },

    copyToClipboard() {
      if (!this.selectedError) return;

      let dataToCopy = null;
      let dataStr = '';

      // Copy data based on currently selected tab
      if (this.activeErrorTab === 'errorOutput') {
        // Copy error details/error output
        dataToCopy = this.getErrorDetailsForTreeView(this.selectedError.error_details) || this.selectedError.error_details || this.selectedError.error_output;
        if (dataToCopy) {
          const parsedData = typeof dataToCopy === 'string' ? this.getJsonValueIfAvailable(dataToCopy) : dataToCopy;
          dataStr = parsedData ? JSON.stringify(parsedData, null, 2) : String(dataToCopy);
        }
      } else if (this.activeErrorTab === 'inputPayload') {
        // Copy input payload
        dataToCopy = this.getJsonValueIfAvailable(this.selectedError.input_payload) || this.selectedError.input_payload;
        if (dataToCopy) {
          const parsedData = typeof dataToCopy === 'string' ? this.getJsonValueIfAvailable(dataToCopy) : dataToCopy;
          dataStr = parsedData ? JSON.stringify(parsedData, null, 2) : String(dataToCopy);
        }
      } else if (this.activeErrorTab === 'fullData') {
        // Copy full error data
        dataToCopy = this.formatFullErrorData(this.selectedError);
        dataStr = JSON.stringify(dataToCopy, null, 2);
      }

      if (!dataStr) {
        this.warningToast('No data available to copy');
        return;
      }

      navigator.clipboard
        .writeText(dataStr)
        .then(() => {
          this.successToast('Copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
          this.errorToast('Failed to copy to clipboard');
        });
    },

    closeRawDataModal() {
      this.showRawDataModal = false;
      this.selectedError = null;
      this.activeErrorTab = 'errorOutput';
    },

    getJsonValueIfAvailable(str) {
      if (!str) return null;
      if (typeof str === 'object' || str.constructor === Array) return str;
      if (typeof str !== 'string') return null;

      try {
        // Try parsing directly first
        return JSON.parse(str);
      } catch (e) {
        // If parsing fails, try unescaping the string (in case it's double-encoded)
        try {
          // Remove extra backslashes that might be from double encoding
          let unescaped = str.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
          // Try parsing the unescaped string
          return JSON.parse(unescaped);
        } catch (e2) {
          // If still fails, try to clean up escaped quotes
          try {
            let cleaned = str.replace(/\\"/g, '"');
            return JSON.parse(cleaned);
          } catch (e3) {
            return null;
          }
        }
      }
    },

    getErrorDetailsForTreeView(errorDetails) {
      // Parse error_details JSON string for tree view display
      if (!errorDetails) return null;
      return this.getJsonValueIfAvailable(errorDetails);
    },

    formatFullErrorData(error) {
      // Format the full error data with proper parsing of JSON strings
      if (!error) return null;

      let formatted = { ...error };

      // Parse error_details if it's a string
      if (formatted.error_details && typeof formatted.error_details === 'string') {
        try {
          formatted.error_details = this.getJsonValueIfAvailable(formatted.error_details);
        } catch (e) {
          // Keep as string if parsing fails
        }
      }

      // Parse input_payload if it's a string
      if (formatted.input_payload && typeof formatted.input_payload === 'string') {
        try {
          formatted.input_payload = this.getJsonValueIfAvailable(formatted.input_payload);
        } catch (e) {
          // Keep as string if parsing fails
        }
      }

      return formatted;
    },

    goBack() {
      this.$router.go(-1);
    }
  },

  beforeDestroy() {
    document.getElementById('subSideMenu').style.display = 'block';
  },

  mounted() {
    document.getElementById('subSideMenu').style.display = 'none';
  },

  created() {
    // Get event name from route params if available
    this.eventName = this.$route.params.eventName || null;

    // Always default to last 7 days
    this.timingFilter = 'last-7';
    this.calculateDateRange();

    this.fetchErrorDetails();
  }
};
